import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ContextOperation } from "../../../../context/OperationContext";
import useSnackbar from "../../../../hooks/useSnackbar";
import useToggle from "../../../../hooks/useToggle";
import FormInput from "../../../common/FormInput";
import { nameof } from "../../../common/ObjectUtils";
import { ContextDamageForm } from "../../DamageFormContext";
import SectionCardActions from "../../SectionCardActions";
import SectionCardHeader from "../../SectionCardHeader";
import DamageFormService from "../../service/DamageFormService";
import { DamageFormTypes } from "../../Types";
import Dialogs from "../dialogs";
import TaskStatus, { ITaskStatus } from "../TaskStatus";
import useDamageFormActions from "../../service/DamageFormActions";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import GeneralInfoCard from "../../generalInfo/GeneralInfoCard";
import { useHasScope } from "../../../../../../config/globalConfig";
import SendIcon from "@mui/icons-material/Send";
import SendBilling from "../dialogs/SendBIllings";
import i18n from "i18next";
import { convertUtcToTzIsoDayJs, revertUtcToTzIsoDayJs } from "../../../../../../util/timezoneConversion";
import { useRecoilValue } from "recoil";
import { generalInfoStepTimezoneState } from "../../creation/GeneralInfoStep";

const Task3 = () => {
  const hasScope = useHasScope;

  const { tenant, setLoading } = useContext(ContextOperation);
  const { task3 } = useContext(ContextDamageForm);
  const damageFormContext = useContext(ContextDamageForm);

  const t = useOperationTranslation();

  const { id } = useParams();

  const snackBar = useSnackbar();

  const [openCloseConfirm, toggleCloseConfirm] = useToggle();
  const [reopenCloseConfirm, toggleReopenConfirm] = useToggle();

  const [toBeChargedConfirm, toggleToBeChargedConfirm] = useToggle();

  const actions = useDamageFormActions();

  const [taskStatus, setTaskStatus] = useState<ITaskStatus>({});

  const [sendBilling, toggleSend] = useToggle();

  const form = useForm<DamageFormTypes.ITask3Update>();
  const timezone = useRecoilValue(generalInfoStepTimezoneState);

  useEffect(() => {
    if (task3) return;

    actions.getTask3();
  }, []);

  const onFormReset = () => {
    form.reset({
      damageFormId: id,
      invoiceAmount: task3?.invoiceAmount,
      clientCharge: task3?.clientCharge,
      damageOwnerSapid: task3?.damageOwnerSapid,
      quotePrice: task3?.quotePrice,
      gridAmount: task3?.gridAmount,
      missingDotationAmount: task3?.missingDotationAmount,
      billingDate: convertUtcToTzIsoDayJs(task3?.billingDate, timezone)?.toISOString(),
    });
  };

  useEffect(() => {
    setTaskStatus({
      open: task3?.open,
      openingDate: task3?.createDate,
      openingUser: task3?.createUsername,
      closeDate: task3?.closeDate,
      closeUser: task3?.closeUsername,
    });

    onFormReset();
  }, [task3]);

  const sendBill = () => {
    toggleSend();
  };

  const convertMoney = (amount: any) => {

    var tmpAmount = '' + amount;

    if (tmpAmount.includes(','))
      amount = amount.replace(',', '.');

    return amount * 100;

  }

  i18n.on('languageChanged', () => {
    actions.getTask2();
    actions.getTask3();
  });

  const onSubmit = (data: DamageFormTypes.ITask3Update) => {
    if (!id) return;

    data.damageFormId = id;

    data.clientCharge = convertMoney(data.clientCharge);
    data.invoiceAmount = convertMoney(data.invoiceAmount);
    data.quotePrice = convertMoney(data.quotePrice);
    data.gridAmount = convertMoney(data.gridAmount);
    data.missingDotationAmount = convertMoney(data.missingDotationAmount);

    data.billingDate = revertUtcToTzIsoDayJs(data.billingDate, timezone)?.toISOString()!;

    setLoading(true);
    DamageFormService.details.task3Update.put.updateTask3(data, tenant).then(
      () => {
        actions.getTask3();
        snackBar.show(
          "success",
          t("damageForm.details.task3.snackbar.updated.success")
        );
        setLoading(false);
      },
      (error) => {
        snackBar.show(
          "error",
          t("damageForm.details.task3.snackbar.updated.error")
        );
        data.clientCharge = data.clientCharge / 100;
        data.invoiceAmount = data.invoiceAmount / 100;
        data.quotePrice = data.quotePrice / 100;
        data.gridAmount = data.gridAmount / 100;
        data.missingDotationAmount = data.missingDotationAmount / 100;
        setLoading(false);
      }
    );
  };

  const onCloseTask3 = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.task3.put.closeById(id).then(
      () => {
        actions.getTask3();
        actions.getGeneralInfo();
        toggleCloseConfirm();
        snackBar.show(
          "success",
          t("damageForm.details.task3.snackbar.close.success")
        );
        setLoading(false);
      },
      () => {
        toggleCloseConfirm();
        snackBar.show(
          "error",
          t("damageForm.details.task3.snackbar.close.error")
        );
        setLoading(false);
      }
    );
  };

  const onReopen = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.task3.put.reopenByid(tenant, id).then(
      () => {
        toggleReopenConfirm();
        actions.getTask3();
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task3.snackbar.reopen.success")
        );
        setLoading(false);
      },
      (error: any) => {
        snackBar.show(
          "error",
          t("damageForm.details.task3.snackbar.reopen.error")
        );

        toggleReopenConfirm();
        setLoading(false);
      }
    );
  };

  const onToBeCharged = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.generalInfo.put.toBeCharged(tenant, id).then(
      () => {
        actions.getGeneralInfo();
        actions.getTask3();
        toggleToBeChargedConfirm();
        snackBar.show(
          "success",
          t("damageForm.details.task3.snackbar.tbc.success")
        );
        setLoading(false);
      },
      () => {
        toggleToBeChargedConfirm();
        snackBar.show(
          "error",
          t("damageForm.details.task3.snackbar.tbc.error")
        );
        setLoading(false);
      }
    );
  };

  return (
    <>
      <Mui.Box mt={2}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Mui.Card>
            <SectionCardHeader>
              <Mui.Grid container spacing={1}>
                {!damageFormContext.generalInfo?.migrated &&
                  hasScope("SCOPE_UPDATE_DAMAGE_TASK_THREE") && (
                    <Mui.Grid item xs="auto">
                      <Mui.Button
                        variant="outlined"
                        size="small"
                        startIcon={<MuiIcons.AttachMoney />}
                        onClick={toggleToBeChargedConfirm}
                        disabled={!(task3?.chargeable &&
                          !damageFormContext.task2?.theftDeductibleCharged)}
                      >
                        <Mui.Typography variant="h6" color="inherit">
                          {t("damageForm.details.task3.header.btnToBeCharged")}
                        </Mui.Typography>
                      </Mui.Button>
                    </Mui.Grid>
                  )}
                {task3?.open &&
                  !damageFormContext.generalInfo?.migrated &&
                  hasScope("SCOPE_UPDATE_DAMAGE_TASK_THREE") && (
                    <Mui.Grid item xs="auto">
                      <Mui.Button
                        variant="outlined"
                        size="small"
                        startIcon={<MuiIcons.MoneyOffCsred />}
                        onClick={toggleCloseConfirm}
                        disabled={task3?.downloadedByFinance}
                      >
                        <Mui.Typography color="inherit" variant="h6">
                          {t("damageForm.details.task3.header.btnNoCharge")}
                        </Mui.Typography>
                      </Mui.Button>
                    </Mui.Grid>
                  )}
                {hasScope("SCOPE_UPDATE_DAMAGE_DAMAGE_FORM") && !damageFormContext.generalInfo?.migrated && (
                  <Mui.Grid item>
                    <Mui.Button
                      component="span"
                      color="accent"
                      onClick={() => sendBill()}
                      startIcon={<SendIcon />}
                      disabled={!task3?.sendableToFinance}
                    >
                      <Mui.Typography variant="h6" color="inherit">
                        {t("damageForm.fotBillingButton")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}
                {task3?.open &&
                  !damageFormContext.generalInfo?.migrated &&
                  hasScope("SCOPE_CLOSE_DAMAGE_DAMAGE_FORM") && (
                    <Mui.Grid item xs="auto" mr={1}>
                      <Mui.Button
                        variant="contained"
                        size="small"
                        startIcon={<MuiIcons.HighlightOffRounded />}
                        onClick={toggleCloseConfirm}
                      >
                        <Mui.Typography color="inherit" variant="h6">
                          {t("damageForm.details.task3.header.btnCloseTask3")}
                        </Mui.Typography>
                      </Mui.Button>
                    </Mui.Grid>
                  )}
                {!task3?.open &&
                  !damageFormContext.generalInfo?.migrated &&
                  hasScope("SCOPE_REOPEN_DAMAGE_TASK_THREE") && (
                    <Mui.Grid item xs="auto">
                      <Mui.Button
                        variant="contained"
                        size="small"
                        startIcon={<MuiIcons.HighlightOffRounded />}
                        onClick={toggleReopenConfirm}
                      >
                        <Mui.Typography color="inherit" variant="h6">
                          {t("damageForm.details.task3.header.btnReopen")}
                        </Mui.Typography>
                      </Mui.Button>
                    </Mui.Grid>
                  )}
              </Mui.Grid>
            </SectionCardHeader>

            <Mui.CardContent>
              <Mui.Stack spacing={2}>
                <GeneralInfoCard />

                <TaskStatus formInfo={taskStatus} />

                <Mui.Accordion>
                  <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />}>
                    <Mui.Typography>
                      {t("damageForm.details.task3.billingDetails.title")}
                    </Mui.Typography>
                  </Mui.AccordionSummary>
                  <Mui.AccordionDetails>
                    <Mui.Grid container spacing={3}>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3Update>("quotePrice")}
                          type="number"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.estimatedCost"
                          )}
                          formInstance={form}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !task3?.open ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_THREE")
                          }
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3Update>(
                            "clientCharge"
                          )}
                          type="number"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.clientCharge"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !task3?.open ||
                            task3.downloadedByFinance
                          }
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3>("chargeDate")}
                          type="date"
                          value={convertUtcToTzIsoDayJs(task3?.chargeDate, timezone)}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.clientChargingDate"
                          )}
                          fullWidth
                          disabled
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3>(
                            "updateClientChargeUser"
                          )}
                          type="text"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.updateClientChargeUser"
                          )}
                          value={task3?.updateClientChargeUser}
                          fullWidth
                          disabled
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3Update>("gridAmount")}
                          type="number"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.damageImport"
                          )}
                          formInstance={form}
                          fullWidth
                          disabled={true}
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3Update>(
                            "missingDotationAmount"
                          )}
                          type="number"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.missingEquipmentImport"
                          )}
                          formInstance={form}
                          fullWidth
                          disabled={true}
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3Update>(
                            "invoiceAmount"
                          )}
                          type="number"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.dmsBillImport"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !task3?.open ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_THREE")
                          }
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3Update>("billingDate")}
                          type="date"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.billingDate"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !task3?.open ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_THREE")
                          }
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3>("realInvoiceAmount")}
                          type="number"
                          value={task3?.realInvoiceAmount}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.realInvoiceAmount"
                          )}
                          fullWidth
                          disabled
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3>("realMissEquipInvoiceAmount")}
                          type="number"
                          value={task3?.realMissEquipInvoiceAmount}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.realMissEquipInvoiceAmount"
                          )}
                          fullWidth
                          disabled
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3>(
                            "billingUser"
                          )}
                          type="text"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.billingUser"
                          )}
                          value={task3?.billingUser}
                          fullWidth
                          disabled
                        />
                      </Mui.Grid>
                      {/*<Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask3>(
                            "damageOwnerSapid"
                          )}
                          type="text"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.damageOwnerSapid"
                          )}
                          formInstance={form}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !task3?.open ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_THREE")
                          }
                        />
                      </Mui.Grid>*/}
                    </Mui.Grid>
                  </Mui.AccordionDetails>
                </Mui.Accordion>
              </Mui.Stack>
            </Mui.CardContent>

            {(hasScope("SCOPE_CREATE_DAMAGE_TASK_THREE") ||
              hasScope("SCOPE_UPDATE_DAMAGE_TASK_THREE")) && (
                <SectionCardActions onClickCancel={onFormReset} />
              )}
          </Mui.Card>
        </form>
      </Mui.Box>

      <Dialogs.CloseTaskConfirmation
        open={openCloseConfirm}
        onClose={toggleCloseConfirm}
        taskName={t("damageForm.details.tabs.task3")}
        onCloseTask={onCloseTask3}
      />

      <Dialogs.ToBeChargedConfirm
        open={toBeChargedConfirm}
        onClose={toggleToBeChargedConfirm}
        taskName={t("damageForm.details.tabs.task3")}
        onCloseTask={onToBeCharged}
      />

      <Dialogs.ReopenTaskConfirmation
        open={reopenCloseConfirm}
        onReopen={toggleReopenConfirm}
        onReopenTask={onReopen}
        taskName={t("damageForm.details.tabs.task3")}
      />

      <SendBilling
        open={sendBilling}
        onClose={toggleSend}
      //damageFormIds={damageFormIds}
      />

    </>
  );
};
export default Task3;
